/* eslint-disable arrow-body-style */
import * as React from "react";
import {Component} from "react";

import {observer} from "mobx-react";

import {Icon} from "../../core/components/icon/icon";
import {Button, Dimmer, Form, Loader, Message, Progress, Select} from "semantic-ui-react";
import {Auftragsverwaltung} from "../../stores/auftragsverwaltung-mobx";

import "./partial-order-form.scss";
import {AppState} from "../../components/app/app-mobx";

import {InputOnChangeData} from "semantic-ui-react/dist/commonjs/elements/Input/Input";
import {PartialOrder} from "../../models/partial-order";
import {Project} from "../../models/project";

export enum StatusType {
    Saving = "saving",
    Warning = "warning",
    Complete = "successful",
    Error = "error"
}

export interface PartialOrderGhkFormProps {
    appStore: AppState;
    auftragsverwaltung: Auftragsverwaltung;
    project: Project;
    onCancel?: (e: React.MouseEvent) => void;
    onSave?: (partialOrder: PartialOrder) => void;
}

@observer
export class PartialOrderGhkForm extends Component<PartialOrderGhkFormProps, any> {
    watchHandles: any[] = [];

    constructor(props: PartialOrderGhkFormProps) {
        super(props);
        this.state = {
            saveError: null,
            saveState: null,
            riskEvidenceEvaluationOptions: null, // available project types,
            riskEvidenceEvaluationId: null,
            riskEvidenceEvaluationIdError: false
        };

        this.handleChange = this.handleChange.bind(this);
        this.handleSave = this.handleSave.bind(this);
    }

    componentDidMount(): void {
        const {auftragsverwaltung} = this.props;
        const partialOrder = auftragsverwaltung.selectedPartialOrderGhk;

        if (!partialOrder) {
            return;
        }

        // pass partial order properties to state. Use controlled state with form controls
        this.setState({
            riskEvidenceEvaluationId: partialOrder.riskEvidenceEvaluationId
        });

        this.loadDomains();
    }

    componentWillUnmount(): void {

    }

    render(): React.ReactElement | null {
        const {auftragsverwaltung} = this.props;
        const partialOrder = auftragsverwaltung.selectedPartialOrderGhk;
        const {
            riskEvidenceEvaluationOptions,
            riskEvidenceEvaluationId,
            saveError,
            saveState,
            riskEvidenceEvaluationIdError
        } = this.state;

        if (!partialOrder) {
            return null;
        }

        if (!riskEvidenceEvaluationOptions) {
            return (
                <div>
                    <Dimmer active inverted>
                        <Loader inverted content="Formulardaten laden" />
                    </Dimmer>
                </div>
            );
        }

        let progress: React.ReactElement | null = null;
        if (saveState) {
            progress = (
                <Progress
                    percent={saveState === StatusType.Complete ? 100 : 50}
                    attached="bottom"
                    active={saveState === StatusType.Saving}
                    warning={saveState === StatusType.Warning}
                    error={saveState === StatusType.Error}
                    success={saveState === StatusType.Complete}
                />
            );
        }

        return (
            <div className="partial-order-form partial-order-form-ghk">
                <Form size="huge" onSubmit={(e) => {
                    e.preventDefault();
                    this.handleSave(partialOrder);
                }}>
                    {riskEvidenceEvaluationOptions &&
                        <Form.Field
                            name="riskEvidenceEvaluationId"
                            control={Select}
                            options={riskEvidenceEvaluationOptions}
                            label={{ children: "Prozess Gefahrenhinweiskarte", htmlFor: "form-select-control-risk-evidence-evaluation" }}
                            placeholder={"z.B. Sturz"}
                            search
                            clearable
                            searchInput={{ id: "form-select-control-risk-evidence-evaluation" }}
                            value={riskEvidenceEvaluationId}
                            onChange={this.handleChange}
                            error={riskEvidenceEvaluationIdError?
                                {
                                    content: "Bitte einen gültigen Prozess Gefahrenhinweiskarte auswählen",
                                }:null
                            }
                        />
                    }
                    {progress}
                    {saveState && saveState === StatusType.Complete &&
                        <Message attached="bottom" positive>
                            <Icon name={"data_floppy_disk"} />
                            Erfolgreich gespeichert!
                        </Message>
                    }
                    {saveState && saveState === StatusType.Error &&
                        <Message attached="bottom" negative>
                            <Icon name={"error"} />
                            Fehler beim Speichern! {saveError}
                        </Message>
                    }
                    <Button className={"save"} primary type="submit">
                        <Icon name={"floppy_disk"} />
                        Speichern
                    </Button>

                    <Button className={"cancel"} secondary
                        onClick={(e: React.MouseEvent) => {
                            if (this.props.onCancel) {
                                this.props.onCancel(e);
                            }
                        }}>
                        <Icon name={"navigate_cross"} />
                        Abbrechen
                    </Button>
                </Form>
            </div>
        );
    }

    handleChange(event: React.ChangeEvent<HTMLInputElement>, data: InputOnChangeData): void {
        this.setState({[data.name]: data.value});
    };

    handleSave(partialOrder: PartialOrder): void {
        const {appStore, auftragsverwaltung, project, onSave} = this.props;
        const {riskEvidenceEvaluationId} = this.state;

        if (!partialOrder) {
            throw new Error("Project undefined saving project");
        }

        const hasError = this.validateFormInput();
        if (hasError) {
            this.setState({formError: true});
            return;
        }

        // make progress indicator visible
        this.setState({
            saveState: StatusType.Saving
        });

        const updated = {
            projectId: project.id,
            riskEvidenceEvaluationId
        };

        const isNewRecord = partialOrder?.isNewRecord;

        const user = appStore.esriCredential?.userId;
        auftragsverwaltung.PartialOrderGhkStore.save(project, partialOrder, updated, user || "")
            .then((pa: PartialOrder) => {
                this.setState({
                    saveState: StatusType.Complete,
                    saveError: null
                });
                if (isNewRecord) {
                    // hide form when a new record was added
                    auftragsverwaltung.setEditing(false);
                }
                if (onSave) {
                    onSave(pa);
                }
            }).catch((error) => {
                this.setState({
                    saveState: StatusType.Error,
                    saveError: error.message
                });
            });
    }

    /**
     * Load domains and sources for select boxes.
     *
     * @private
     */
    private loadDomains() {
        const {auftragsverwaltung} = this.props;
        auftragsverwaltung.getDomainAsOptions(
            "partial-order-ghk",
            "riskEvidenceEvaluationId"
        ).then((riskEvidenceEvaluationOptions) =>{
            this.setState({
                riskEvidenceEvaluationOptions
            });
        });
    }

    private validateFormInput(): boolean {
        const {
            riskEvidenceEvaluationId,
        } = this.state;

        const validates = {
            riskEvidenceEvaluationIdError: false,
        };
        let isError: boolean = false;

        // mandatory field
        if (!riskEvidenceEvaluationId) {
            validates.riskEvidenceEvaluationIdError = true;
            isError = true;
        }

        this.setState(validates);
        return isError;
    }

}
